// Consent Pending Page - Stage 04 - VISUALLY UPDATED with InsureVision Colors
// Holding page while waiting for DocuSign signature
// FIXED: Use effectiveUid for polling to support admin impersonation
// UPDATED: Added blur/focus detection for immediate check when user returns
// UPDATED: Added demo mode support with "Simulate Signature" button
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../../context/OnboardingContext'
import { useDemoMode } from '../../hooks/useDemoMode'
import { n8nApi } from '../../services/n8nApi'

const ConsentPendingPage = () => {
  const navigate = useNavigate()
  const { STAGES, syncFromServer, effectiveUid, transitionToStage } = useOnboarding()
  const { isDemoMode, simulateSuccess } = useDemoMode()
  const [isSimulating, setIsSimulating] = useState(false)

  // Track whether user has left the page (e.g., to sign in DocuSign)
  const hasLeftPage = useRef(false)

  // Handle demo mode "Simulate Signature" button
  const handleSimulateSignature = () => {
    setIsSimulating(true)
    simulateSuccess(() => {
      transitionToStage(STAGES.CONSENT_SIGNED)
      navigate('/onboarding/consent-complete')
    }, 1500)
  }

  // Poll for status changes
  useEffect(() => {
    // DEMO MODE: Skip polling
    if (isDemoMode) return

    const checkStatus = async () => {
      if (!effectiveUid) return
      
      try {
        const response = await n8nApi.getUserStatus(effectiveUid)
        if (response.found && response.prospect) {
          const prospect = response.prospect
          
          // Check if stage has advanced
          if (prospect.onboarding_stage === STAGES.CONSENT_SIGNED ||
              prospect.onboarding_stage === STAGES.API_SETUP) {
            
            console.log('✅ Consent signed detected, redirecting to API setup...')

            // Use hard navigation to force full app re-mount and fresh Airtable fetch
            // This avoids race condition where navigate() fires before React state updates
            window.location.href = '/onboarding/api-setup'
            return // Stop further processing
            
          }
        }
      } catch (error) {
        console.error('Error checking status:', error)
      }
    }

    // Handle user leaving the page (e.g., clicking DocuSign link)
    const handleWindowBlur = () => {
      hasLeftPage.current = true
      console.log('👋 User left page, will check for signature on return')
    }

    // Handle user returning to the page
    const handleWindowFocus = async () => {
      if (!hasLeftPage.current) return
      
      console.log('🔄 User returned, checking for signature...')
      hasLeftPage.current = false
      
      // Immediate check when user returns
      await checkStatus()
    }

    // Check immediately on mount
    checkStatus()

    // Poll every 10 seconds as fallback
    const interval = setInterval(checkStatus, 10000)

    // Add focus/blur listeners
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      clearInterval(interval)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [effectiveUid, navigate, STAGES, syncFromServer, isDemoMode])

  return (
    <div className="max-w-xl mx-auto text-center animate-fade-in">
      {/* Clock/waiting icon with brand colors */}
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center">
        <svg className="w-10 h-10 text-brand-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      
      <h1 className="text-2xl font-display font-bold text-brand-950 mb-3">
        Awaiting Signature
      </h1>
      
      <p className="text-slate-600 mb-6">
        The Data Access Agreement has been sent for signature. Once signed, we can begin 
        analyzing your dashcam footage to generate your personalized risk assessment.
      </p>

      {/* Demo mode: Simulate Signature button */}
      {isDemoMode ? (
        <button
          onClick={handleSimulateSignature}
          disabled={isSimulating}
          className="btn-accent px-6 py-3"
        >
          {isSimulating ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Simulating...
            </span>
          ) : (
            'Simulate Signature'
          )}
        </button>
      ) : (
        /* Subtle polling indicator with accent color */
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
          <svg className="animate-spin h-4 w-4 text-accent-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Checking for signature...</span>
        </div>
      )}

      {/* Info card */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-200 text-left">
        <h2 className="font-semibold text-brand-950 mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          What happens next?
        </h2>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="text-accent-600 mt-0.5">•</span>
            <span>The signatory will receive the agreement via DocuSign email</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-600 mt-0.5">•</span>
            <span>We will send you an email once this has been signed, and you'll then be able to set up API access to your dashcam provider</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-600 mt-0.5">•</span>
            <span>Our AI will analyze your fleet footage to identify claim risk patterns</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

export default ConsentPendingPage
